import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, TextField, Typography, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaceIcon from '@mui/icons-material/Place';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import FunctionPageHeader from '../components/FunctionPageHeader';
import {
  getMyLocations, createMyLocation, updateMyLocation, deleteMyLocation,
  type MyLocation,
} from '../services/eeMyLocationsService';
import { formatLocalDate } from '../utils/dateHelper';

const FUNC_CODE = 'ee_my_locations';

interface FormData { name: string; lat: string; lng: string; note: string; }

const emptyForm = (): FormData => ({ name: '', lat: '', lng: '', note: '' });

const isValidLat = (v: string) => v === '' || (isFinite(+v) && +v >= -90  && +v <= 90);
const isValidLng = (v: string) => v === '' || (isFinite(+v) && +v >= -180 && +v <= 180);

const EeMyLocationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();

  const [locations, setLocations]     = useState<MyLocation[]>([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState<number | null>(null);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editing, setEditing]         = useState<MyLocation | null>(null);
  const [formData, setFormData]       = useState<FormData>(emptyForm());
  const [confirmId, setConfirmId]     = useState<number | null>(null);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMyLocations();
      setLocations(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => {
    if (!permissionLoading && hasPermission(FUNC_CODE, 'read')) {
      loadLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionLoading]);

  const setField = (k: keyof FormData, v: string) =>
    setFormData(prev => ({ ...prev, [k]: v }));

  const openAdd = () => {
    setEditing(null);
    setFormData(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (loc: MyLocation) => {
    setEditing(loc);
    setFormData({
      name: loc.name,
      lat:  loc.lat !== 0 ? String(loc.lat) : '',
      lng:  loc.lng !== 0 ? String(loc.lng) : '',
      note: loc.note ?? '',
    });
    setDialogOpen(true);
  };

  const isFormValid =
    !!formData.name.trim() &&
    isValidLat(formData.lat) &&
    isValidLng(formData.lng);

  const handleSave = async () => {
    if (!isFormValid) return;
    const lat = formData.lat.trim() !== '' ? parseFloat(formData.lat) : 0;
    const lng = formData.lng.trim() !== '' ? parseFloat(formData.lng) : 0;
    setSaving(true);
    try {
      if (editing) {
        await updateMyLocation(editing.id, {
          name: formData.name.trim(), lat, lng, note: formData.note,
        });
        setLocations(prev => prev.map(l =>
          l.id === editing.id
            ? { ...l, name: formData.name.trim(), lat, lng, note: formData.note }
            : l,
        ));
        showSuccess('SYS020001', { name: formData.name.trim() });
      } else {
        const newLoc = await createMyLocation({
          name: formData.name.trim(), lat, lng, note: formData.note,
        });
        setLocations(prev => [...prev, newLoc]);
        showSuccess('SYS020001', { name: formData.name.trim() });
      }
      setDialogOpen(false);
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeleting(id);
    try {
      await deleteMyLocation(id);
      setLocations(prev => prev.filter(l => l.id !== id));
      showSuccess('SYS020003', { name: '' });
    } catch (err) {
      showApiError(err);
    } finally {
      setDeleting(null);
      setConfirmId(null);
    }
  };

  if (permissionLoading) return <CircularProgress sx={{ m: 4 }} />;
  if (!hasPermission(FUNC_CODE, 'read')) return <Typography sx={{ m: 4 }}>無權限</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <FunctionPageHeader funcCode={FUNC_CODE} fallback="地點管理" />

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        {hasPermission(FUNC_CODE, 'create') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} size="small">
            新增地點
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                <TableCell>名稱</TableCell>
                <TableCell>緯度</TableCell>
                <TableCell>經度</TableCell>
                <TableCell>備註</TableCell>
                <TableCell>建立時間</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    尚無地點
                  </TableCell>
                </TableRow>
              ) : (
                locations.map(loc => (
                  <TableRow key={loc.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PlaceIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        {loc.name}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {loc.lat !== 0 ? loc.lat.toFixed(5) : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {loc.lng !== 0 ? loc.lng.toFixed(5) : '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{loc.note || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {formatLocalDate(loc.created_at)}
                    </TableCell>
                    <TableCell align="right">
                      {hasPermission(FUNC_CODE, 'update') && (
                        <Tooltip title="編輯">
                          <IconButton size="small" onClick={() => openEdit(loc)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {hasPermission(FUNC_CODE, 'delete') && (
                        <Tooltip title="刪除">
                          <IconButton size="small" color="error" onClick={() => setConfirmId(loc.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? '編輯地點' : '新增地點'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="地點名稱" required autoFocus
              value={formData.name}
              onChange={e => setField('name', e.target.value)}
              size="small" fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="緯度" size="small" fullWidth
                value={formData.lat}
                onChange={e => setField('lat', e.target.value)}
                error={!isValidLat(formData.lat)}
                helperText={!isValidLat(formData.lat) ? '-90 ~ 90' : ''}
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">N/S</Typography>
                  </InputAdornment>,
                  sx: { fontFamily: 'monospace', fontSize: 13 },
                }}
              />
              <TextField
                label="經度" size="small" fullWidth
                value={formData.lng}
                onChange={e => setField('lng', e.target.value)}
                error={!isValidLng(formData.lng)}
                helperText={!isValidLng(formData.lng) ? '-180 ~ 180' : ''}
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">E/W</Typography>
                  </InputAdornment>,
                  sx: { fontFamily: 'monospace', fontSize: 13 },
                }}
              />
            </Box>
            <TextField
              label="備註" multiline rows={2}
              value={formData.note}
              onChange={e => setField('note', e.target.value)}
              size="small" fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>取消</Button>
          <Button
            variant="contained"
            disabled={!isFormValid || saving}
            onClick={handleSave}
          >
            {saving ? <CircularProgress size={18} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!confirmId} onClose={() => setConfirmId(null)} maxWidth="xs">
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>確定要刪除這個地點嗎？此操作無法復原。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)}>取消</Button>
          <Button
            color="error" variant="contained"
            disabled={deleting !== null}
            onClick={() => confirmId !== null && handleDelete(confirmId)}
          >
            {deleting !== null ? <CircularProgress size={18} /> : '刪除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EeMyLocationsPage;